import React, { useMemo } from 'react'
import { Dropdown } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { ActionsToggle } from '../Actions/CrossClientActionsDropdown'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { ReactComponent as NoMatchIcon } from '../../../assets/images/ccq/group-not-in-records.svg'
import { type CrossClientQueryAtomicClause, type CrossClientQuerySuggestedDecomposition, type PredefinedCrossClientQuery } from '../../../models/CrossClient'
import { pluralize } from '../utils'
import { ccqCriterionClauses } from '../Results/Run/ccqResultView'
import { CrossClientPredefinedQueryItem } from './Predefined/CrossClientPredefinedQueryItem'
import { type CrossClientReviewState } from './hooks'
import { RECOMMENDED_SAMPLE_SIZE } from './recommendedSample'

interface CrossClientChatReviewProps {
  reviewState: CrossClientReviewState
  recommendedQueries?: PredefinedCrossClientQuery[]
  onSelectRecommendedQuery?: (query: PredefinedCrossClientQuery) => void
  onSeeAllRecommendedQueries?: () => void
  runDisabled?: boolean
}

type TimelineItem =
  | { kind: 'lead'; key: string; content: React.ReactNode }
  | { kind: 'criteria'; key: string; clauses: CrossClientQueryAtomicClause[] }
  | { kind: 'note'; key: string; content: React.ReactNode }
  | { kind: 'result'; key: string; icon: 'no-match'; title: React.ReactNode; description?: React.ReactNode }
  | {
      kind: 'recommended'
      key: string
      queries: PredefinedCrossClientQuery[]
      onSelect: (query: PredefinedCrossClientQuery) => void
      onSeeAll: () => void
      runDisabled: boolean
    }

export const CrossClientChatReview: React.FC<CrossClientChatReviewProps> = ({
  reviewState,
  recommendedQueries,
  onSelectRecommendedQuery,
  onSeeAllRecommendedQueries,
  runDisabled,
}) => {
  const items = useMemo(
    () => buildTimeline(reviewState, recommendedQueries, onSelectRecommendedQuery, onSeeAllRecommendedQueries, runDisabled),
    [reviewState, recommendedQueries, onSelectRecommendedQuery, onSeeAllRecommendedQueries, runDisabled],
  )

  return (
    <div className='d-flex flex-column gap-3 cross-client-breakdown'>
      {items.map((item, index) => (
        <TimelineRow key={item.key} item={item} index={index} />
      ))}
    </div>
  )
}

const clauseKey = (clause: CrossClientQueryAtomicClause): string => clause.variable || clause.questionForm

interface CrossClientChatReviewActionsProps {
  reviewState: CrossClientReviewState
  runDisabled?: boolean
  onRetry: () => void
  onRestart: () => void
  onFindClients: () => void
  onEditQuestionText: (question: string) => void
  onUseOriginal: () => void
  onUseSuggested: (suggestion: CrossClientQuerySuggestedDecomposition) => void
}

interface SuggestionFooterActionsProps {
  suggestion: CrossClientQuerySuggestedDecomposition
  canUseOriginal: boolean
  onRestart: () => void
  onEditQuestionText: (question: string) => void
  onUseOriginal: () => void
  onUseSuggested: (suggestion: CrossClientQuerySuggestedDecomposition) => void
}

const SuggestionFooterActions: React.FC<SuggestionFooterActionsProps> = ({
  suggestion,
  canUseOriginal,
  onRestart,
  onEditQuestionText,
  onUseOriginal,
  onUseSuggested,
}) => (
  <div className='d-flex gap-2'>
    <Dropdown drop='up' align='start'>
      <ActionsToggle variant='header' className='cross-client-suggestion-menu-toggle rounded-3 h-100' aria-label='More actions'>
        <i className='bi bi-three-dots' />
      </ActionsToggle>
      <Dropdown.Menu className='cross-client-suggestion-menu rounded-3 border-0 py-1'>
        {canUseOriginal ? (
          <Dropdown.Item className='paragraph-medium py-2 px-3' onClick={onUseOriginal}>
            Use original question
          </Dropdown.Item>
        ) : null}
        <Dropdown.Item className='paragraph-medium py-2 px-3' onClick={onRestart}>
          Ask another question
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
    <Button variant='header' className='button-medium cross-client-chat-action' onClick={() => onEditQuestionText(suggestion.userQuestion)}>
      Edit suggested
    </Button>
    <Button variant='primary' className='button-medium cross-client-chat-action' onClick={() => onUseSuggested(suggestion)}>
      Use suggested
    </Button>
  </div>
)

export const CrossClientChatReviewActions: React.FC<CrossClientChatReviewActionsProps> = ({
  reviewState,
  runDisabled,
  onRetry,
  onRestart,
  onFindClients,
  onEditQuestionText,
  onUseOriginal,
  onUseSuggested,
}) => {
  if (reviewState.kind === 'confirm') {
    return (
      <div className='d-flex gap-2'>
        <Button variant='header' className='button-medium cross-client-chat-action' onClick={onRestart}>
          Refine question
        </Button>
        <Button variant='primary' className='button-medium cross-client-chat-action' onClick={onFindClients} disabled={runDisabled}>
          Find clients
        </Button>
      </div>
    )
  }
  if (reviewState.kind === 'rejected' || reviewState.kind === 'refinement') {
    const { suggestion } = reviewState
    if (!suggestion) {
      return (
        <div className='d-flex gap-2'>
          <Button variant='header' className='button-medium cross-client-chat-action' onClick={onRestart}>
            Ask another question
          </Button>
        </div>
      )
    }
    return (
      <SuggestionFooterActions
        suggestion={suggestion}
        canUseOriginal={reviewState.kind === 'refinement'}
        onRestart={onRestart}
        onEditQuestionText={onEditQuestionText}
        onUseOriginal={onUseOriginal}
        onUseSuggested={onUseSuggested}
      />
    )
  }
  return (
    <div className='d-flex gap-2'>
      <Button variant='header' className='button-medium cross-client-chat-action' onClick={onRestart}>
        Refine question
      </Button>
      <Button variant='primary' className='button-medium cross-client-chat-action' onClick={onRetry} disabled={runDisabled}>
        Try again
      </Button>
    </div>
  )
}

const ZocksBulletLogo: React.FC = () => (
  <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25 cross-client-breakdown__bullet--zocks'>
    <ZocksLogo width={12} height={12} />
  </div>
)

// Drives the answer's cascade-in delay (see `.cross-client-breakdown__row` in CrossClientLanding.scss).
const staggerStyle = (name: '--cross-client-answer-row-index' | '--cross-client-answer-item-index', index: number) => ({ [name]: index }) as React.CSSProperties

const ClauseRow: React.FC<{ clause: CrossClientQueryAtomicClause }> = ({ clause }) => (
  <div className='d-flex align-items-center gap-2 cross-client-breakdown__criterion'>
    <i className='bi bi-x-diamond-fill cross-client-breakdown__criterion-icon' />
    <span>{clause.questionForm || clause.description}</span>
  </div>
)

const TimelineRow: React.FC<{ item: TimelineItem; index: number }> = ({ item, index }) => {
  const rowStyle = staggerStyle('--cross-client-answer-row-index', index)
  switch (item.kind) {
    case 'lead':
      return (
        <div
          className='position-relative d-flex align-items-start zocks-gap-0.75 cross-client-breakdown__row cross-client-breakdown__step cross-client-breakdown__step--lead'
          style={rowStyle}
        >
          <ZocksBulletLogo />
          <div className='paragraph-medium mb-0 cross-client-breakdown__lead-text'>{item.content}</div>
        </div>
      )
    case 'criteria':
      return (
        <ul className='position-relative d-flex flex-column gap-3 cross-client-breakdown__row cross-client-breakdown__criteria' style={rowStyle}>
          {item.clauses.map((clause, clauseIndex) => (
            <li key={clauseKey(clause)} className='cross-client-breakdown__criteria-item' style={staggerStyle('--cross-client-answer-item-index', clauseIndex)}>
              <ClauseRow clause={clause} />
            </li>
          ))}
        </ul>
      )
    case 'note':
      return (
        <div className='paragraph-medium mb-0 cross-client-breakdown__row cross-client-breakdown__note' style={rowStyle}>
          {item.content}
        </div>
      )
    case 'recommended':
      return (
        <div className='d-flex flex-column zocks-gap-0.5 zocks-pl-1.75 z-1 cross-client-breakdown__row' style={rowStyle}>
          <div className='paragraph-medium bold'>Try a different question or pick from the query library:</div>
          <ul className='list-unstyled mb-0 d-flex flex-column zocks-gap-0.375'>
            {item.queries.map((query) => (
              <li key={query.id}>
                <CrossClientPredefinedQueryItem
                  predefinedQuery={query}
                  bodyText={query.question}
                  layout='row'
                  onClick={item.onSelect}
                  runDisabled={item.runDisabled}
                />
              </li>
            ))}
          </ul>
          <Button variant='primaryLinkButton' className='align-self-start button-link-small bold text-decoration-none' onClick={item.onSeeAll}>
            Browse the query library
          </Button>
        </div>
      )
    case 'result':
      return (
        <div className='position-relative d-flex align-items-start zocks-gap-0.75 cross-client-breakdown__row cross-client-breakdown__step' style={rowStyle}>
          <NoMatchIcon width={16} height={16} className='cross-client-breakdown__step-icon' />
          <div className='d-flex flex-column gap-1'>
            <div className='paragraph-medium mb-0'>{item.title}</div>
            {item.description ? <div className='paragraph-medium mb-0 zocks-text-gray-700'>{item.description}</div> : null}
          </div>
        </div>
      )
  }
}

const SUGGESTION_LEAD_IN = "Here's a version I can run:"

const buildSuggestionLead = (explanation?: string): string => (explanation ? `${explanation} ${SUGGESTION_LEAD_IN}` : SUGGESTION_LEAD_IN)

const buildSuggestionItems = (keyPrefix: string, suggestion: CrossClientQuerySuggestedDecomposition, leadText: string): TimelineItem[] => [
  { kind: 'lead', key: `${keyPrefix}:lead`, content: leadText },
  {
    kind: 'note',
    key: `${keyPrefix}:suggested-question`,
    content: (
      <>
        <strong>Suggested question:</strong> {suggestion.userQuestion}
      </>
    ),
  },
  {
    kind: 'note',
    key: `${keyPrefix}:change-note`,
    content: (
      <>
        <strong>Assumption:</strong> {suggestion.changeNote}
      </>
    ),
  },
  { kind: 'note', key: `${keyPrefix}:check-intro`, content: "Here's what I'll check for each client:" },
  { kind: 'criteria', key: `${keyPrefix}:criteria`, clauses: ccqCriterionClauses(suggestion) },
]

const buildTimeline = (
  state: CrossClientReviewState,
  recommendedQueries?: PredefinedCrossClientQuery[],
  onSelectRecommendedQuery?: (query: PredefinedCrossClientQuery) => void,
  onSeeAllRecommendedQueries?: () => void,
  runDisabled?: boolean,
): TimelineItem[] => {
  const clauses = state.atomicClauses
  const count = clauses.length
  const noun = pluralize(count, 'criterion', 'criteria')

  const introContent = (
    <>
      <strong>{count}</strong> {noun} found.
    </>
  )

  switch (state.kind) {
    case 'rejected': {
      if (state.suggestion) {
        return buildSuggestionItems('rejected', state.suggestion, buildSuggestionLead(state.message))
      }
      const items: TimelineItem[] = [{ kind: 'lead', key: 'rejected:lead', content: state.message }]
      if (recommendedQueries && recommendedQueries.length > 0 && onSelectRecommendedQuery && onSeeAllRecommendedQueries) {
        items.push({
          kind: 'recommended',
          key: 'rejected:recommended',
          queries: recommendedQueries.slice(0, RECOMMENDED_SAMPLE_SIZE),
          onSelect: onSelectRecommendedQuery,
          onSeeAll: onSeeAllRecommendedQueries,
          runDisabled: !!runDisabled,
        })
      }
      return items
    }
    case 'refinement':
      return buildSuggestionItems('refinement', state.suggestion, buildSuggestionLead(state.suggestion.reason))
    case 'confirm':
      return [
        { kind: 'lead', key: 'confirm:lead', content: "I'll look for clients who match these criteria:" },
        { kind: 'criteria', key: 'confirm:criteria', clauses },
        { kind: 'note', key: 'confirm:note', content: 'Anything to adjust, or good to go?' },
      ]
    case 'trigger-failed':
      return [
        { kind: 'lead', key: 'trigger-failed:intro', content: introContent },
        { kind: 'criteria', key: 'trigger-failed:criteria', clauses },
        {
          kind: 'result',
          key: 'trigger-failed:error',
          icon: 'no-match',
          title: <strong>I couldn't start checking your clients.</strong>,
          description: 'Try again, or refine the question.',
        },
      ]
  }
}
